import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Box } from '@chakra-ui/react'
import { colors } from '../../../utilities/colors'
import { NeutralButton } from '../../common/Buttons'
import ReusableDrawer from '../../common/ReusableDrawer'
import Spinner from '../../common/Spinner'
import { WizardHeader } from '../../ui/wizard'
import useStore from '../../../store'
import useTransportationBasePath from '../../../hooks/useTransportationBasePath'
import LocationStep from './wizard/LocationStep'
import LocationSummary from './wizard/LocationSummary'
import VehiclesStep from './wizard/VehiclesStep'
import ReviewStep from './wizard/ReviewStep'
import { emptyLocation } from '../../../constants/carpoolLocation'
import DrawerErrorBanner from '../shared/DrawerErrorBanner'

export default function CreateQuote({ units, isOpen, onClose }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const basePath = useTransportationBasePath()
  const {
    createCarpoolQuote,
    dealer,
    setCarpoolQuote,
    fetchAuctions,
    fetchCarpoolDealerPlaces,
  } = useStore()

  const steps = useMemo(() => [t('Origin'), t('Destination'), t('Vehicles'), t('Review')], [t])

  const originSuggestion = useMemo(() => {
    if (!units?.length) return null

    const anyOnLot = units.some((u) => u.attributes?.location === 'On The Lot')
    if (anyOnLot) return { mode: 'lot' }

    const auctionIds = units.map((u) => u.attributes?.['auction-id']).filter(Boolean).map(String)
    if (auctionIds.length === 0) return null

    const counts = new Map()
    for (const id of auctionIds) {
      counts.set(id, (counts.get(id) || 0) + 1)
    }
    const [mostCommonId] = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]
    return { mode: 'auction', auctionId: mostCommonId }
  }, [units])

  const [stepIndex, setStepIndex] = useState(0)
  const [origin, setOrigin] = useState({ ...emptyLocation })
  const [destination, setDestination] = useState({ ...emptyLocation })
  const [allOperable, setAllOperable] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState(null)
  const originStepRef = useRef(null)
  const destinationStepRef = useRef(null)

  const currentStep = steps[stepIndex]
  const isOriginStep = currentStep === t('Origin')
  const isDestinationStep = currentStep === t('Destination')
  const isVehiclesStep = currentStep === t('Vehicles')
  const isReviewStep = currentStep === t('Review')

  useEffect(() => {
    if (isOpen) {
      setIsSubmitting(false)
      setSubmitError(null)
      useStore.setState({ isCreatingCarpoolQuote: false })
      if (dealer?.id) {
        fetchAuctions()
        fetchCarpoolDealerPlaces()
      }
    }
  }, [isOpen, dealer?.id, fetchAuctions, fetchCarpoolDealerPlaces])

  const reset = () => {
    setStepIndex(0)
    setOrigin({ ...emptyLocation })
    setDestination({ ...emptyLocation })
    setAllOperable(true)
    setCarpoolQuote(null)
  }

  const handleCancel = () => {
    reset()
    onClose()
  }

  const handleNext = () => {
    if (isOriginStep && !originStepRef.current?.isValid()) return
    if (isDestinationStep && !destinationStepRef.current?.isValid()) return
    setStepIndex((index) => Math.min(index + 1, steps.length - 1))
  }

  const handleBack = () => {
    setStepIndex((index) => {
      const next = Math.max(index - 1, 0)
      if (isDestinationStep) setDestination({ ...emptyLocation })
      return next
    })
  }

  const buildLocationPayload = (location, prefix) => {
    const params = {
      city: location.city,
      state: location.state,
      postal_code: location.postal_code != null ? String(location.postal_code) : '',
    }

    const payload = { [`${prefix}_params`]: params }

    if (location.type === 'Auction' || location.type === 'DealerPlace') {
      payload[`${prefix}_type`] = location.type
      payload[`${prefix}_id`] = location.id
      payload[`${prefix}_location_type`] = location.type === 'Auction' ? 'auction' : (location.place_type || 'dealer')
    } else if (location.type) {
      payload[`${prefix}_location_type`] = location.type
    }

    return payload
  }

  const handleConfirm = async () => {
    const payload = {
      carpool_quote: {
        ...buildLocationPayload(origin, 'origin'),
        ...buildLocationPayload(destination, 'destination'),
        vehicles: units.map((unit) => ({
          vin: unit.attributes.vin,
          inoperable: !allOperable,
        })),
      },
      dealer_id: dealer.id,
    }
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      const quote = await createCarpoolQuote(payload)
      onClose()
      reset()
      navigate(`${basePath}/quotes/${quote.id}`)
    } catch (error) {
      setSubmitError(error?.message || t('Error creating carpool quote.'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const renderBody = () => {
    if (isSubmitting) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" w="full" py={10}>
          <Spinner />
        </Box>
      )
    }
    return (
      <Box display="flex" flexDirection="column" gap={4} w="full" py={2}>
        <DrawerErrorBanner message={submitError} onDismiss={() => setSubmitError(null)} />
        <WizardHeader
          title={currentStep}
          progress={((stepIndex + 1) / steps.length) * 100}
          showBackButton={false}
        />
        {isOriginStep && (
          <LocationStep
            ref={originStepRef}
            key="origin-step"
            kind="origin"
            value={origin}
            onChange={setOrigin}
            accentColor={colors.yellow}
            suggestion={originSuggestion}
            excludedValue={destination}
            unitCount={units.length}
          />
        )}
        {isDestinationStep && (
          <>
            <LocationSummary label={t('From')} value={origin} accent={colors.yellow} />
            <LocationStep
              ref={destinationStepRef}
              key="destination-step"
              kind="destination"
              value={destination}
              onChange={setDestination}
              accentColor="#4A90D9"
              excludedValue={origin}
              suggestion={origin.type === 'Auction' ? { mode: 'lot' } : null}
            />
          </>
        )}
        {isVehiclesStep && (
          <VehiclesStep
            units={units}
            allOperable={allOperable}
            onAllOperableChange={setAllOperable}
          />
        )}
        {isReviewStep && (
          <ReviewStep
            origin={origin}
            destination={destination}
            units={units}
            allOperable={allOperable}
          />
        )}
      </Box>
    )
  }

  const footer = isSubmitting ? null : (
    <Box display="flex" gap={2} my={4} justifyContent="center" w="full">
      {stepIndex > 0 && <NeutralButton text={t('Back')} onClick={handleBack} />}
      {!isReviewStep && <NeutralButton text={t('Next')} onClick={handleNext} />}
      {isReviewStep && (
        <NeutralButton text={t('Confirm')} onClick={handleConfirm} />
      )}
      <NeutralButton text={t('Cancel')} onClick={handleCancel} />
    </Box>
  )

  return (
    <ReusableDrawer
      isOpen={isOpen}
      onClose={handleCancel}
      placement="bottom"
      title={t('Get Transport Quote')}
      bgColor={colors.grey900}
      size={{ base: 'full', md: 'lg' }}
      titleStyle="flex items-center justify-center w-full"
      bodyStyle="flex flex-col items-center justify-start overflow-y-auto"
      footerStyle="flex items-center justify-center w-full"
      bodyContent={renderBody()}
      footerContent={footer}
    />
  )
}
